import { Card, LoadingView, OnboardingLoading, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { AccountStep } from './AccountStep';
import { BillingStep } from './BillingStep';
import { Button, Image, Label, useAuth } from '@app/engine';
import { CommonConstants, FormatLocalized, Logger } from '@shared/common';
import { Telemetry, TenantClient } from '@shared/frontend';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IPhoneNumberCandidate, OnboardingStep } from '@shared/common';
import type { ReactNode } from 'react';

/** Onboarding step values (the gated progress states persisted on the tenant). */
const Step = CommonConstants.Database.OnboardingStep;

/** Per-row checklist status. Drives both the row styling and the status badge. */
type ChecklistStatus = 'done' | 'current' | 'pending' | 'failed';

/** File-local constants for the onboarding wizard. */
const localConstants = {
  logContext: 'OnboardingWizard',
  // Glyph shown in the indicator for a completed step. A literal check mark (not an escape sequence).
  doneGlyph: '✓',
  // The three gated steps, in order. Each entry pairs the persisted step value with the localization keys for its checklist row; the visible copy is resolved per render so it reflows on a
  // language change (see `checklistRows` inside the component). `Complete` is the terminal state (the dashboard opens) and is not a row.
  checklistKeys: [
    { step: Step.Account, nameKey: 'OnboardingStepAccountName', hintKey: 'OnboardingStepAccountHint' },
    { step: Step.Billing, nameKey: 'SharedBilling', hintKey: 'OnboardingStepBillingHint' },
    { step: Step.Phone, nameKey: 'OnboardingStepPhoneName', hintKey: 'OnboardingStepPhoneHint' }
  ],
  // Progress index of each step. `Billing` with verified billing is treated as the phone step (index 2) so the checklist advances the moment payment lands, before the phone call resolves.
  phoneIndex: 2,
  // The `prepare`-endpoint outcome that means the AI Number was allocated outright (no owner choice needed); the other outcome surfaces the Twilio candidate picker.
  provisionModeProvisioned: 'provisioned',
  // Maximum preferred numbers the owner may pick on the Twilio picker path (first choice + one fallback), matching the server-side cap.
  maxSelectedNumbers: 2
} as const;

/**
 * Maps a step value to its zero-based progress index.
 *
 * @param step - The onboarding step (or null before the first status read).
 * @returns The progress index (account 0, billing 1, phone 2, complete 3).
 */
function StepIndexOf(step: OnboardingStep | null): number {
  if (step === Step.Billing) return 1;
  if (step === Step.Phone) return localConstants.phoneIndex;
  if (step === Step.Complete) return localConstants.phoneIndex + 1;
  return 0;
}

/**
 * Resolves the badge class and label for a checklist status.
 *
 * @param status - The checklist status.
 * @returns The badge className and its localized label.
 */
function BadgeFor(status: ChecklistStatus): { className: string; label: string } {
  if (status === 'done') return { className: `${Styles.badge} ${Styles.badgeSuccess}`, label: Localized.SharedDone };
  if (status === 'current') return { className: `${Styles.badge} ${Styles.badgePending}`, label: Localized.SharedInProgress };
  if (status === 'failed') return { className: `${Styles.badge} ${Styles.badgeDanger}`, label: Localized.OnboardingStatusFailed };
  return { className: Styles.badge, label: Localized.SharedPending };
}

/**
 * Resolves the row modifier class for a checklist status.
 *
 * @param status - The checklist status.
 * @returns The combined row className.
 */
function RowClassFor(status: ChecklistStatus): string {
  if (status === 'done') return `${Styles.wizardStep} ${Styles.wizardStepDone}`;
  if (status === 'current') return `${Styles.wizardStep} ${Styles.wizardStepCurrent}`;
  if (status === 'failed') return `${Styles.wizardStep} ${Styles.wizardStepFailed}`;
  return `${Styles.wizardStep} ${Styles.wizardStepPending}`;
}

/**
 * Phone step of the onboarding wizard: the third and final gated step. On mount it asks the server (via the onboarding/phone/prepare endpoint) how the AI Receptionist Phone Number (AI Number)
 * should be allocated. That endpoint is server-authoritative — it re-verifies the subscription and decides — so this component never decides anything itself; it only triggers the call and
 * renders the outcome. Two outcomes:
 *
 * - `provisioned` — onboarding is already complete (idempotent short-circuit: prior completion, or a reused number from an earlier attempt): clear the stashed area codes and call onComplete.
 * - `select` — the provider returned Twilio candidates: render a picker so the owner chooses up to two preferred numbers, then provision the selection (tried in order, falling back to the
 *   area-code search) via the onboarding/phone endpoint. On success, same completion path.
 *
 * The AI Number is the line the tenant's clients call; it is distinct from the owner's Contact Number, which is collected on the account step. A failure is never silently recoverable: an
 * actionable error keeps the owner on this step with a retry.
 *
 * @param props - Component props.
 * @param props.tenantID - The onboarding tenant whose AI Number to provision.
 * @param props.onComplete - Invoked once onboarding completes so the wizard advances and the gate opens.
 * @param props.onFailedChange - Reports provisioning failure so the wizard marks the phone row as needing action.
 * @returns The phone-step panel element.
 */
function PhoneStep({ tenantID, onComplete, onFailedChange }: { tenantID: string; onComplete: () => void; onFailedChange: (failed: boolean) => void }) {
  const { contactEmail } = useLobbySettings();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // The Twilio candidates to choose from (empty until a `select` outcome lands). A non-empty list switches the panel from the spinner to the picker.
  const [candidates, setCandidates] = useState<IPhoneNumberCandidate[]>([]);
  // The owner's ordered preferred numbers (E.164), capped at the selection maximum. First entry is tried first; the rest are fallbacks.
  const [selected, setSelected] = useState<string[]>([]);
  // True while the prepare or the selection-provision call is in flight, so the panel shows the spinner and the picker actions disable.
  const [busy, setBusy] = useState(true);
  // Bumped by the retry button to re-run the prepare effect.
  const [attempt, setAttempt] = useState(0);

  // Phase 1: ask the server how the AI Number should be allocated. A `provisioned` outcome completes onboarding outright; a `select` outcome surfaces the Twilio picker.
  useEffect(() => {
    onFailedChange(false);
    setError('');
    setCandidates([]);
    setSelected([]);
    setBusy(true);
    const controller = new AbortController();
    // The preferred area codes are an optional CSV hint stashed by the account step. Absent on a cold resume is fine — the provider then searches without one.
    const areaCodes = sessionStorage.getItem(Constants.OnboardingAreaCodeStorageKey) ?? '';
    const body = areaCodes.length > 0 ? { areaCodes } : {};
    TenantClient.PreparePhone(controller.signal, body)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.mode === localConstants.provisionModeProvisioned) {
          // Allocated outright: onboarding is complete server-side. Drop the hint and advance.
          sessionStorage.removeItem(Constants.OnboardingAreaCodeStorageKey);
          Telemetry.OnboardingStepCompleted(Step.Phone);
          onComplete();
          return;
        }
        // Candidate list: let the owner pick. An empty list means no inventory — surface an actionable error rather than an empty picker.
        if (result.candidates.length === 0) {
          onFailedChange(true);
          setError(FormatLocalized(Localized.OnboardingPhonePickEmpty, contactEmail));
          return;
        }
        setCandidates(result.candidates);
        setBusy(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // The AI Number must be provisioned before onboarding completes. Drop the stashed hint so a manual Retry searches afresh, then surface an actionable error and keep the owner on this step.
        sessionStorage.removeItem(Constants.OnboardingAreaCodeStorageKey);
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        onFailedChange(true);
        setError(FormatLocalized(Localized.OnboardingPhoneError, contactEmail));
      });
    return () => controller.abort();
  }, [tenantID, attempt, onComplete, onFailedChange, contactEmail]);

  // Toggle a candidate in the ordered selection: deselect if already chosen, otherwise append while under the cap (clicks past the cap are ignored so the order stays stable).
  const toggleCandidate = useCallback((e164: string) => {
    setError('');
    setSelected((prev) => {
      if (prev.includes(e164)) return prev.filter((value) => value !== e164);
      if (prev.length >= localConstants.maxSelectedNumbers) return prev;
      return [...prev, e164];
    });
  }, []);

  // Phase 2 (Twilio path): provision the owner's selected numbers (tried in order, falling back to the area-code search),
  // then complete onboarding on success. No selection is valid: it provisions straight from the area-code preferences via the standard search.
  const onContinue = useCallback(() => {
    if (busy) return;
    onFailedChange(false);
    setError('');
    setBusy(true);
    const controller = new AbortController();
    const areaCodes = sessionStorage.getItem(Constants.OnboardingAreaCodeStorageKey) ?? '';
    const body = { ...(selected.length > 0 ? { selectedNumbers: selected } : {}), ...(areaCodes.length > 0 ? { areaCodes } : {}) };
    TenantClient.ProvisionPhone(controller.signal, body)
      .then(() => {
        if (controller.signal.aborted) return;
        sessionStorage.removeItem(Constants.OnboardingAreaCodeStorageKey);
        Telemetry.OnboardingStepCompleted(Step.Phone);
        onComplete();
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        setBusy(false);
        setError(FormatLocalized(Localized.OnboardingPhonePickContinueError, contactEmail));
      });
  }, [selected, busy, onComplete, onFailedChange, contactEmail]);

  // Abandons onboarding: signs the visitor out and returns them to the public landing page.
  const onLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    }
    navigate(Constants.Route.Landing);
  }, [logout, navigate]);

  // The picker shows once candidates land and there is no hard error. Until then (or on error) the panel is the spinner/error layout with a Retry.
  const showPicker = candidates.length > 0 && error.length === 0;

  if (showPicker) {
    return (
      <Card title={Localized.OnboardingPhonePickTitle} className={Styles.onboardingCard}>
        <Label classNameContainer={Styles.muted} text={Localized.OnboardingPhonePickSubtitle} />
        <div className={Styles.numberPicker}>
          {candidates.map((candidate) => {
            const rank = selected.indexOf(candidate.e164);
            const isSelected = rank >= 0;
            const meta = [candidate.locality, candidate.region].filter((value): value is string => typeof value === 'string' && value.length > 0).join(', ');
            // Raw <button>: engine Button is text-only; this option renders a rank badge, number Label, and locality Label with a selected state.
            return (
              <button
                key={candidate.e164}
                type="button"
                className={isSelected ? `${Styles.numberOption} ${Styles.numberOptionSelected}` : Styles.numberOption}
                onClick={() => toggleCandidate(candidate.e164)}
                disabled={busy}
              >
                {isSelected ? <Label classNameContainer={Styles.numberOptionRank} text={String(rank + 1)} /> : null}
                {/* Raw <span>: structural wrapper grouping the number and locality Labels inside the option button; engine Label renders text only. */}
                <span className={Styles.numberOptionBody}>
                  <Label classNameContainer={Styles.numberOptionLabel} text={candidate.e164} />
                  {meta.length > 0 ? <Label classNameContainer={Styles.numberOptionMeta} text={meta} /> : null}
                </span>
              </button>
            );
          })}
        </div>
        <div className={Styles.gateActions}>
          <Button text={Localized.DashSignOut} type="button" classNameButtonEnabled={Styles.btn} onClickHandler={() => void onLogout()} />
          <Button
            text={Localized.OnboardingPhonePickContinue}
            type="button"
            disabled={busy}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary}`}
            onClickHandler={onContinue}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card title={Localized.OnboardingPhoneTitle} className={Styles.onboardingCard}>
      <Label classNameContainer={Styles.muted} text={Localized.OnboardingPhoneSubtitle} />
      {error.length === 0 ? (
        <>
          <LoadingView className={Styles.onboardingLoading} />
          <Label classNameContainer={Styles.gateNote} text={Localized.OnboardingPhoneProvisioning} />
        </>
      ) : (
        <Label classNameContainer={Styles.errorText} text={error} />
      )}
      <div className={Styles.gateActions}>
        <Button text={Localized.DashSignOut} type="button" classNameButtonEnabled={Styles.btn} onClickHandler={() => void onLogout()} />
        {error.length > 0 ? <Button text={Localized.SharedTryAgain} classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`} onClickHandler={() => setAttempt((value) => value + 1)} /> : null}
      </div>
    </Card>
  );
}

/**
 * Server-authoritative, fail-closed onboarding wizard. It is the only thing the dashboard gate renders for an authenticated account until onboarding is 100% complete: the dashboard nav and
 * content never appear before every gated step (our own data, verified Stripe billing, and a provisioned AI Number) has succeeded. A persistent checklist shows each step's state (done / in
 * progress / pending / needs action) above the active step panel, and the flow is fully resumable — the server's onboarding status decides which step renders, so a returning owner always lands
 * on the exact step they left off. The steps run in order: account (creates tenant + owner + profile, no vendor call), billing (hosted Stripe checkout; the wizard advances only once the billing
 * webhook persists a subscription), then phone (provisions the AI Receptionist Phone Number after billing is verified server-side). Any status-read failure shows a try-again panel — it never
 * falls through to the dashboard.
 *
 * @returns The onboarding wizard element.
 */
export function OnboardingWizard() {
  const { onboardingChecked, onboardingStep, onboardingError, onboardingTenantID, billingChecked, billingAccess, tenantID, refreshTenant, refreshOnboarding } = useDashboard();
  const { contactEmail } = useLobbySettings();
  const { getThemedAssetDTO } = useTheme();
  const { langVersion } = useLocale();
  const navigate = useNavigate();
  const [phoneFailed, setPhoneFailed] = useState(false);
  // The Vertex Echo wordmark (icon + product name baked in), matching the dashboard sidebar brand.
  const logoAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'Logo.png'), [getThemedAssetDTO]);

  // Resolve the checklist row copy per language change so the wizard reflows with the rest of the app (the step values stay constant; only the displayed name/hint are localized).
  const checklistRows = useMemo(() => {
    void langVersion;
    return localConstants.checklistKeys.map((row) => ({ step: row.step, name: Localized[row.nameKey], hint: Localized[row.hintKey] }));
  }, [langVersion]);

  // Re-resolve every gate driver after a step succeeds: onboarding status advances to the next step (or to complete, opening the gate), and tenant resolution repopulates so the dashboard has the
  // owner's tenant once it finally renders. The billing read cascades automatically off the resolved tenant inside the provider.
  const advance = useCallback(() => {
    refreshTenant();
    refreshOnboarding();
  }, [refreshTenant, refreshOnboarding]);

  // Billing is verified once the provider has read a non-null subscription status. This is only a UI signal for which panel to show; the phone endpoint re-verifies billing server-side regardless.
  const billingVerified = billingChecked && (billingAccess?.status ?? null) !== null;
  // Prefer the tenant from the onboarding status (known the moment the account exists) over the GetMe-resolved tenant, so billing/phone can start before GetMe settles.
  const resolvedTenantID = onboardingTenantID ?? tenantID;

  const baseIndex = StepIndexOf(onboardingStep);
  const effectiveIndex = onboardingStep === Step.Billing && billingVerified ? localConstants.phoneIndex : baseIndex;
  // The current step is the one that failed when the status read errored (any step) or the phone provisioning failed (phone step).
  const failedIndex = onboardingError ? effectiveIndex : phoneFailed ? localConstants.phoneIndex : -1;

  // Resolve which step panel to render. Fail-closed: a status error shows the try-again panel; a billing step whose access has not resolved yet holds on a spinner rather than flashing checkout.
  let panel: ReactNode;
  if (onboardingError) {
    panel = (
      <Card className={Styles.onboardingCard}>
        <Label classNameContainer={Styles.errorText} text={FormatLocalized(Localized.OnboardingLoadFailed, contactEmail)} />
        <div className={Styles.gateActions}>
          <Button text={Localized.SharedTryAgain} classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`} onClickHandler={() => refreshOnboarding()} />
        </div>
      </Card>
    );
  } else if (onboardingStep === Step.Complete) {
    // Terminal state — the gate renders the dashboard, not the wizard. Rendered defensively for the brief window before the gate re-evaluates.
    panel = null;
  } else if (onboardingStep === Step.Phone || (onboardingStep === Step.Billing && billingVerified)) {
    panel = resolvedTenantID ? <PhoneStep tenantID={resolvedTenantID} onComplete={advance} onFailedChange={setPhoneFailed} /> : <OnboardingLoading />;
  } else if (onboardingStep === Step.Billing) {
    panel = !billingChecked ? <OnboardingLoading /> : resolvedTenantID ? <BillingStep tenantID={resolvedTenantID} /> : <OnboardingLoading />;
  } else {
    panel = <AccountStep onComplete={advance} />;
  }

  return (
    <div className={Styles.root}>
      <div className={Styles.onboardingShell}>
        <div className={Styles.wizardColumn}>
          <Card className={Styles.onboardingCard}>
            <Image
              title={Localized.DashName}
              assetDTO={logoAssetDTO}
              classNameContainer={Styles.onboardingHeaderRow}
              classNameImageEnabled={Styles.brandWordmark}
              onClickHandler={() => navigate(Constants.Route.Landing)}
            />
            <div className={Styles.wizardChecklist}>
              {checklistRows.map((row, index) => {
                const status: ChecklistStatus = index < effectiveIndex ? 'done' : index === effectiveIndex ? (index === failedIndex ? 'failed' : 'current') : 'pending';
                const badge = BadgeFor(status);
                return (
                  <div key={row.step} className={RowClassFor(status)}>
                    <Label classNameContainer={Styles.wizardStepIndex} text={status === 'done' ? localConstants.doneGlyph : String(index + 1)} />
                    <div className={Styles.wizardStepBody}>
                      <Label classNameContainer={Styles.wizardStepTitle} text={row.name} />
                      <Label classNameContainer={Styles.wizardStepHint} text={row.hint} />
                    </div>
                    <Label classNameContainer={`${Styles.wizardStepStatus} ${badge.className}`} text={badge.label} />
                  </div>
                );
              })}
            </div>
          </Card>
          {onboardingChecked ? panel : <OnboardingLoading />}
        </div>
      </div>
    </div>
  );
}
